import VerifiedIcon from "@mui/icons-material/DoneOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { Box, Chip, Switch, Typography } from "@mui/material";
import { capitalize } from "lodash";
import { FlexCenterWrapper } from "../../../../../../../../components/DefaultNoDataWrapper";
import {
  CHART_COLORS,
  CHART_DIMENSIONS,
  CHART_TYPOGRAPHY,
  CardHeader,
  DataCard,
  DataCardSection,
  DataFieldRow,
} from "../../../../../../../../components/app/provider/patient/chart";
import { NoInsCardAvailableIcon } from "../../../../../../../../components/svg-icons/NoinsCardIcon";
import { formatDate } from "../../../../../../../../utils/date-time-utility";
import {
  getAddressFormat,
  getValueFromObj,
  replaceUnderScoreAddSpace,
} from "../../../../../../../../utils/utils";

interface InsuranceCardProps {
  item: any;
  onUpdatePatientInsuranceToPrimary?: (item: any) => void;
  onEditInsurance?: (item: any) => void;
  onViewCardDetails?: (item: any) => void;
  disableAction?: boolean;
  minimized?: boolean;
}

const InsuranceCard = ({
  item,
  onUpdatePatientInsuranceToPrimary,
  onEditInsurance,
  onViewCardDetails,
  disableAction = false,
  minimized = false,
}: InsuranceCardProps) => {
  const title = item?.orderOfBenefits
    ? `${
        item?.orderOfBenefits === "TERNARY"
          ? "Tertiary"
          : capitalize(item?.orderOfBenefits)
      } Insurance`
    : "-";

  const subscriberName =
    item?.subscriberFirstName || item?.subscriberLastName
      ? `${item.subscriberFirstName ?? ""} ${item.subscriberLastName ?? ""}`.trim()
      : "";

  const hasImages = item?.frontPhoto || item?.backPhoto;

  const sections: DataCardSection[] = [
    {
      rows: [
        {
          cells: [
            <DataFieldRow
              label="Payer Name"
              value={item?.insurancePayer?.payerName || "-"}
            />,
            <DataFieldRow
              label="Coverage type"
              value={item?.coverageType || "-"}
            />,
            <DataFieldRow
              label="Provider"
              value={getValueFromObj(item?.provider as any) || "-"}
            />,
          ],
        },
        {
          cells: [
            <DataFieldRow label="Plan Name" value={item?.planName || "-"} />,
            <DataFieldRow label="INS ID" value={item?.insuranceId || "-"} />,
            <DataFieldRow
              label="Effe. Start Date"
              value={formatDate(item?.effectiveStartDate) || "-"}
            />,
          ],
        },
        {
          cells: [
            <DataFieldRow label="Plan Type" value={item?.planType || "-"} />,
            <DataFieldRow label="Group ID" value={item?.groupId || "-"} />,
            <DataFieldRow
              label="Effe. End Date"
              value={formatDate(item?.effectiveEndDate) || "-"}
            />,
          ],
        },
        {
          cells: [
            <DataFieldRow
              label="Deductible"
              value={item?.deductible != null ? String(item.deductible) : "-"}
            />,
            <DataFieldRow
              label="Deductible Remaining"
              value={item?.deductibleAmount || "-"}
            />,
            <Box />,
          ],
        },
      ],
    },
    {
      sectionHeader: "Subscriber Information",
      rows: [
        {
          cells: [
            <DataFieldRow
              label="Relationship to Insured"
              value={
                replaceUnderScoreAddSpace(
                  item?.insuredRelationshipWithPatient?.toLowerCase()
                ) || "-"
              }
            />,
            <DataFieldRow label="Name" value={subscriberName || "-"} />,
            <DataFieldRow
              label="Date Of Birth"
              value={formatDate(item?.subscriberBirthDate) || "-"}
            />,
          ],
        },
        {
          cells: [
            <DataFieldRow
              label="Contact No."
              value={item?.subscriberMobileNumber || "-"}
            />,
            <DataFieldRow label="SSN" value={item?.subscriberSsn || "-"} />,
            <DataFieldRow
              label="Gender"
              value={capitalize(item?.subscriberGender) || "-"}
            />,
          ],
        },
        {
          cells: [
            <DataFieldRow
              label="Address"
              value={getAddressFormat(item?.subscriberAddress) || "-"}
              stretchValue
            />,
            <Box />,
            <Box />,
          ],
        },
      ],
    },
  ];

  const isPrimary = item?.orderOfBenefits === "PRIMARY";

  // ECRH-5111 #6.2: surface a "Verified" badge when the insurance has a
  // successful eligibility check on file.
  const verifiedBadge = item?.eligibilityVerified ? (
    <Chip
      size="small"
      icon={<VerifiedIcon sx={{ fontSize: 14 }} />}
      label="Verified"
      sx={{
        bgcolor: "#e8f5e9",
        color: "#2e7d32",
        "& .MuiChip-icon": { color: "#2e7d32" },
      }}
    />
  ) : null;

  const markAsPrimary = !isPrimary ? (
    <Box display="flex" alignItems="center" gap={0.5}>
      <Switch
        size="small"
        checked={isPrimary}
        onChange={() => onUpdatePatientInsuranceToPrimary?.(item)}
        disabled={disableAction}
      />
      <Typography
        sx={{
          ...CHART_TYPOGRAPHY.body5,
          color: CHART_COLORS.textDark,
        }}
      >
        Mark as Primary
      </Typography>
    </Box>
  ) : null;

  const headerExtra =
    verifiedBadge || markAsPrimary ? (
      <Box display="flex" alignItems="center" gap={1}>
        {verifiedBadge}
        {markAsPrimary}
      </Box>
    ) : undefined;

  return (
    <Box
      sx={{
        border: `1px solid ${CHART_COLORS.border}`,
        borderRadius: CHART_DIMENSIONS.borderRadius,
        overflow: "hidden",
      }}
    >
      <CardHeader
        title={title}
        onEdit={disableAction ? undefined : () => onEditInsurance?.(item)}
        headerExtra={headerExtra}
      />
      <Box
        display="grid"
        gridTemplateColumns={minimized ? "1fr" : "702fr 357fr"}
      >
        <Box minWidth={0}>
          <DataCard
            title={title}
            sections={sections}
            hideHeader
            sx={{
              border: "none",
              borderRadius: 0,
            }}
          />
        </Box>
        {!minimized && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 1.5,
              borderLeft: `1px solid ${CHART_COLORS.border}`,
              backgroundColor: CHART_COLORS.surfaceWhite,
            }}
          >
            <Box display="flex" gap={1.5} alignItems="center">
              {hasImages ? (
                <>
                  {item?.frontPhoto && (
                    <Box
                      component="img"
                      src={item.frontPhoto}
                      alt="Insurance card front"
                      sx={{
                        width: "159px",
                        height: "100px",
                        objectFit: "cover",
                        borderRadius: "12px",
                        border: `0.5px solid ${CHART_COLORS.primary}`,
                      }}
                    />
                  )}
                  {item?.backPhoto && (
                    <Box
                      component="img"
                      src={item.backPhoto}
                      alt="Insurance card back"
                      sx={{
                        width: "159px",
                        height: "100px",
                        objectFit: "cover",
                        borderRadius: "12px",
                        border: `0.5px solid ${CHART_COLORS.primary}`,
                      }}
                    />
                  )}
                </>
              ) : (
                <FlexCenterWrapper>
                  <NoInsCardAvailableIcon />
                  <Typography
                    sx={{
                      ...CHART_TYPOGRAPHY.body5,
                      color: CHART_COLORS.textLabel,
                    }}
                  >
                    Card Not Uploaded
                  </Typography>
                </FlexCenterWrapper>
              )}
            </Box>
            {hasImages && (
              <Box
                display="flex"
                alignItems="center"
                gap={0.5}
                sx={{ cursor: "pointer" }}
                onClick={() => onViewCardDetails?.(item)}
              >
                <VisibilityOutlinedIcon
                  sx={{
                    fontSize: CHART_DIMENSIONS.iconSm,
                    color: CHART_COLORS.primary,
                  }}
                />
                <Typography
                  sx={{
                    ...CHART_TYPOGRAPHY.body5,
                    color: CHART_COLORS.primary,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  View Details
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default InsuranceCard;
